import { FormControlLabel, Grid, Radio, RadioGroup, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { Language, PolicyData } from '../../../api';
import { FormTextField } from '../../../experienceSettings/formComponents/FormTextField';
import { FormRichTextField } from '../../../experienceSettings/formComponents/FormRichTextField';

export enum PolicyType {
  EXTERNAL = 'external',
  INLINE = 'inline',
}

export type PolicyFormData = {
  type: PolicyType;
  external_url: string;
  content: { [key in Language]?: string };
};

export const policyToFormData = (policy?: PolicyData, languages?: Language[]): PolicyFormData => {
  const emptyContent = languages?.reduce((acc, lang) => ({ ...acc, [lang]: '' }), {}) || {};

  if (policy?.external_url) {
    return { type: PolicyType.EXTERNAL, external_url: policy.external_url, content: emptyContent };
  }
  if (policy?.content) {
    const content = languages?.reduce((acc, lang) => ({ ...acc, [lang]: policy.content?.[lang] || '' }), {}) || {};
    return {
      type: PolicyType.INLINE,
      external_url: '',
      content,
    };
  }
  return { type: PolicyType.EXTERNAL, external_url: '', content: emptyContent };
};

export const formDataToPolicy = (data: PolicyFormData): PolicyData | undefined => {
  if (data.type === PolicyType.EXTERNAL && data.external_url) {
    return { external_url: data.external_url };
  }
  const hasContent = Object.values(data.content).some((v) => v);
  if (data.type === PolicyType.INLINE && hasContent) {
    return { content: data.content };
  }
  return undefined;
};

type PolicyEditorProps = {
  title: string;
  value: PolicyFormData;
  onChange: (value: PolicyFormData) => void;
  fieldNamePrefix: string;
  disabled: boolean;
};

export const PolicyEditor = ({ title, value, onChange, fieldNamePrefix, disabled }: PolicyEditorProps) => {
  const { t } = useTranslation();

  return (
    <Grid
      container
      flexDirection="column"
      gap={2}
      sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: '8px' }}>
      <Typography fontWeight="600">{title}</Typography>
      <RadioGroup row value={value.type} onChange={(e) => onChange({ ...value, type: e.target.value as PolicyType })}>
        <FormControlLabel
          value={PolicyType.EXTERNAL}
          control={<Radio disabled={disabled} />}
          label={t('business_details.external_link')}
        />
        <FormControlLabel
          value={PolicyType.INLINE}
          control={<Radio disabled={disabled} />}
          label={t('business_details.inline_content')}
        />
      </RadioGroup>

      {value.type === PolicyType.EXTERNAL && (
        <FormTextField
          label={t('business_details.external_link')}
          fieldName={`${fieldNamePrefix}.external_url`}
          placeholder="https://"
          isFieldUpdate
          disabled={disabled}
          width={'100%'}
        />
      )}

      {value.type === PolicyType.INLINE && (
        <FormRichTextField labelFieldName={`${fieldNamePrefix}.content`} disabled={disabled} />
      )}
    </Grid>
  );
};
